import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { useMsal, useAccount } from '@azure/msal-react';
import {
  useCompleteComplianceItemMutation,
  useGetComplianceItemByIdQuery,
} from '../../../../services/ComplianceService/complianceApi';
import { ChecklistItemsList } from '../ChecklistItems';
import { ChecklistItemView } from '../../../../services/ComplianceService/types/complianceTypes';

interface ComplianceCompleteDialogProps {
  /** The item being completed; the dialog is open whenever this is non-null. */
  complianceItemId: string | null;
  onClose: () => void;
}

/**
 * ComplianceCompleteDialog - Captures completion details (who, when, vendor, optional verifier)
 * and marks a compliance requirement complete. Shared by the dashboard grid's quick-action button
 * and the edit page's Completion Tracking section (US4, T051/T052).
 */
export const ComplianceCompleteDialog: React.FC<ComplianceCompleteDialogProps> = ({
  complianceItemId,
  onClose,
}) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [completeComplianceItem, { isLoading }] = useCompleteComplianceItemMutation();
  const { data: complianceItem } = useGetComplianceItemByIdQuery(complianceItemId ?? '', {
    skip: !complianceItemId,
  });

  const [completedDate, setCompletedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [isVendorPerformed, setIsVendorPerformed] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [verifiedBy, setVerifiedBy] = useState('');
  const [verifiedDate, setVerifiedDate] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [checklistItems, setChecklistItems] = useState<ChecklistItemView[]>([]);

  // EOC items carry a non-empty checklist; keep the working copy in sync with the currently
  // targeted item (research.md #3 — recorded in the same operation as completion).
  useEffect(() => {
    setChecklistItems(complianceItem?.checklistItems ?? []);
  }, [complianceItem]);

  const resetForm = () => {
    setCompletedDate(new Date().toISOString().slice(0, 10));
    setIsVendorPerformed(false);
    setVendorName('');
    setVerifiedBy('');
    setVerifiedDate('');
    setErrorMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!complianceItemId) return;

    try {
      const userName = account?.name || 'Unknown User';
      await completeComplianceItem({
        id: complianceItemId,
        completedBy: userName,
        completedDate: completedDate ? new Date(completedDate).toISOString() : undefined,
        isVendorPerformed,
        vendorName,
        verifiedBy: verifiedBy || undefined,
        verifiedDate: verifiedDate ? new Date(verifiedDate).toISOString() : undefined,
        modifiedBy: userName,
        checklistItems: checklistItems.length > 0 ? checklistItems : undefined,
      }).unwrap();

      handleClose();
    } catch (error) {
      console.error('Failed to complete compliance item:', error);
      setErrorMessage('Failed to mark this requirement complete. Please try again.');
    }
  };

  return (
    <Dialog open={complianceItemId !== null} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mark Requirement Complete</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextField
            label="Completed Date"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={completedDate}
            onChange={(e) => setCompletedDate(e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isVendorPerformed}
                onChange={(e) => setIsVendorPerformed(e.target.checked)}
              />
            }
            label="Performed by an outside vendor"
          />

          {isVendorPerformed && (
            <TextField
              label="Vendor Name"
              size="small"
              fullWidth
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
            />
          )}

          <TextField
            label="Verified By (optional)"
            size="small"
            fullWidth
            value={verifiedBy}
            onChange={(e) => setVerifiedBy(e.target.value)}
          />

          <TextField
            label="Verified Date (optional)"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={verifiedDate}
            onChange={(e) => setVerifiedDate(e.target.value)}
            disabled={!verifiedBy}
          />

          {checklistItems.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Checklist Items
              </Typography>
              <ChecklistItemsList items={checklistItems} onChange={setChecklistItems} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          sx={{ backgroundColor: '#162346', '&:hover': { backgroundColor: '#1a2a52' } }}
        >
          {isLoading ? 'Saving...' : 'Mark Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplianceCompleteDialog;
